import {useCallback, useState} from "react";
import {type GraphQLQuery} from "@aws-amplify/api-graphql";
import {useAmplifyClients} from "../AmplifyClientProvider";
import {
  AmplifyClientKey,
  GraphqlResult,
  GraphqlVariables,
  MutationTrigger,
  toError
} from "./types";

interface UseGraphqlMutationOptions<
  TData,
  TVariables extends GraphqlVariables
> {
  /** Amplify mutation string */
  amplifyMutation: string;
  /** Which Amplify client to use (defaults to "appsyncBff") */
  amplifyClient?: AmplifyClientKey;
}

/**
 * Amplify-only mutation hook returning [mutateFn, { data, loading, error }].
 */
export function useGraphqlMutation<
  TData = unknown,
  TVariables extends GraphqlVariables = GraphqlVariables
>(
  options: UseGraphqlMutationOptions<TData, TVariables>
): [MutationTrigger<TData, TVariables>, GraphqlResult<TData>] {
  const clients = useAmplifyClients();
  const client = clients?.[options.amplifyClient ?? "appsyncBff"] ?? null;
  const [amplifyData, setAmplifyData] = useState<TData | undefined>(undefined);
  const [amplifyLoading, setAmplifyLoading] = useState(false);
  const [amplifyError, setAmplifyError] = useState<Error | undefined>(
    undefined
  );

  const amplifyMutate: MutationTrigger<TData, TVariables> = useCallback(
    async (mutateOptions?) => {
      const vars = mutateOptions?.variables;
      setAmplifyLoading(true);
      setAmplifyError(undefined);

      try {
        if (!client) {
          throw new Error("Amplify client not initialized");
        }

        const result = await client.graphql<GraphQLQuery<TData>>({
          query: options.amplifyMutation,

          variables: vars as any
        });
        const data = result.data as TData;

        setAmplifyData(data);
        setAmplifyLoading(false);
        return {data};
      } catch (err) {
        const error = toError(err);
        setAmplifyError(error);
        setAmplifyLoading(false);
        throw error;
      }
    },

    [client, options.amplifyMutation]
  );

  return [
    amplifyMutate,
    {data: amplifyData, loading: amplifyLoading, error: amplifyError}
  ];
}
